import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, AuthUser } from '../api/auth';
import {
  setAccessToken,
  clearAuthStorage,
  refreshAccessToken,
} from '../api/client';
import { toast } from 'sonner';

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (loginIdentifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Bootstrap: при монтировании пытаемся обменять refresh-cookie на access_token.
  // access_token живёт только в памяти, поэтому при F5 страницу нужно
  // переподнять — refresh-cookie это позволяет без явного логина.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await refreshAccessToken();
      if (cancelled) return;
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const response = await authApi.getCurrentUser();
        if (cancelled) return;
        if (response.user.role !== 'admin') {
          toast.error('Access denied. Admin role required.');
          await doLogout();
          return;
        }
        setUser(response.user);
      } catch (error) {
        console.error('Auth bootstrap failed:', error);
        clearAuthStorage();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authApi.getCurrentUser();
      if (response.user.role !== 'admin') {
        toast.error('Access denied. Admin role required.');
        await doLogout();
        return;
      }
      setUser(response.user);
    } catch (error) {
      console.error('Auth check failed:', error);
      await doLogout();
    }
  };

  const login = async (loginIdentifier: string, password: string) => {
    try {
      const response = await authApi.login({ loginIdentifier, password });

      if (response.user.role !== 'admin') {
        // Очищаем созданную бэком сессию — это не админ.
        await authApi.logout();
        clearAuthStorage();
        throw new Error('Access denied. Admin role required.');
      }

      setAccessToken(response.access_token);
      setUser(response.user);
      toast.success('Successfully logged in!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  const doLogout = async () => {
    await authApi.logout();
    clearAuthStorage();
    setUser(null);
  };

  const logout = async () => {
    await doLogout();
    toast.success('Logged out successfully');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
